'use client';

import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { IconWrapper } from 'components/icon-wrapper';
import { IconLoading02 } from 'icon/General/loading-02';
import { IconLock01 } from 'icon/Security/lock-01';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next-nprogress-bar';
import { AffixedInput, Button, toast } from 'ui';

export type VerifyPasswordProps =
  | {
      subdomain: string;
    }
  | {
      postId: string;
    };

export function VerifyPassword(props: VerifyPasswordProps): JSX.Element {
  const [password, setPassword] = React.useState('');
  const t = useTranslations('Blog.Password');
  const router = useRouter();
  const options = {
    onError: () => {
      toast.error(t('Error'));
    },
    onSuccess: (data: { pass: boolean }) => {
      if (data.pass) {
        toast.success(t('Success'));
        router.refresh();
        return;
      }
      toast.error(t('Error'));
    },
  };
  const { mutate: verify, isPending } = useMutation({
    mutationFn: async () => {
      // Can't use trpc here because cookies can't be set by trpc
      const res = await fetch('/.quotion/api/password', {
        method: 'POST',
        body: JSON.stringify({ ...props, password }),
      });
      if (!res.ok) {
        throw new Error('Failed to verify password');
      }
      const rsp = (await res.json()) as { pass: boolean };
      return rsp;
    },
    ...options,
  });
  const isPost = !!(props as { postId: string }).postId;
  return (
    <div className="password-gate freeze-body">
      <form
        className="password-card"
        onSubmit={(e) => {
          // Handle enter key, but ignore default submit
          e.preventDefault();
          verify();
        }}
      >
        <IconWrapper className="password-icon">
          <IconLock01 />
        </IconWrapper>
        <div className="password-copy">
          <h1 className="password-title">
            {isPost ? t('Post.Title') : t('Site.Title')}
          </h1>
          <p className="password-description">
            {isPost ? t('Post.Description') : t('Site.Description')}
          </p>
          <AffixedInput
            className="password-input"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('Placeholder')}
          />
        </div>
        <div className="password-actions">
          <Button
            className="password-submit"
            type="submit"
            disabled={isPending}
          >
            {isPending && (
              <IconLoading02
                className="password-loading"
                height={20}
                width={20}
              />
            )}
            {t('Button')}
          </Button>
        </div>
      </form>
    </div>
  );
}
